import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityOrderBy,
  FeatureName,
  formatShortDate,
  getEffectiveCreationDate,
  Issue,
  IssueType,
  replaceMentionPlaceholderWithNames,
  Severity,
  useFindByQuery,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Text } from 'react-native-paper';
import { Layout } from '../../styles';
import { Colors } from '../../themes/colors';
import { TypeDropdown, type TypeVariant } from '../crop-circle/Components/Type';
import { HeaderDot } from '../HeaderDot';
import LoadingIndicator from '../loadingIndicator';
import { getIssueAuditData } from './issueFormatUtils';
import { IssueHeaderOld } from './issueHeader-old';
import { IssueMenu } from './issueMenu';
import { ReminderSummary } from './reminderSummary';

// Mapping between Severity and TypeVariant for crop circle redesign
const severityToVariant: Record<Severity, TypeVariant> = {
  [Severity.Red]: 'attention',
  [Severity.Yellow]: 'blocker',
  [Severity.Green]: 'done',
  [Severity.Purple]: 'note',
  [Severity.Blue]: 'routines',
};

const variantToSeverity: Record<TypeVariant, Severity> = {
  attention: Severity.Red,
  blocker: Severity.Yellow,
  done: Severity.Green,
  note: Severity.Purple,
  routines: Severity.Blue,
  urgent: Severity.Red,
  pending: Severity.Yellow,
  solved: Severity.Green,
  campaign: Severity.Purple,
};

export type IssueHeaderProps = {
  issue: Issue;
  onChanged: (severity: string) => void;
  setShowFooter: (show: boolean) => void;
  titleStyle?: StyleProp<ViewStyle>;
  viewAssetInfo?: () => void;
  onIssueDelete: () => void;
  hideMenu?: boolean;
  onPrint?: () => Promise<void>;
};

const IssueHeaderBase = (props: IssueHeaderProps) => {
  const { t } = useTranslation();
  const issue = props.issue;
  if (!issue) {
    return;
  }

  const getProcessedTitle = () => {
    const titleText = issue.title;

    // Process mentions to ensure clean format
    if (titleText.includes('@[') && (issue as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (issue as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const issueId = issue.id;

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true
          }
        }
      ],
      json: { 'trigger.config.issueId': issueId },
    };
  }, [issueId]);

  const { data: workflows, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const hasReminder = useMemo(() => workflows?.items?.length > 0, [workflows]);

  const workflow = useMemo(() => workflows?.items[0], [workflows]);

  const createdAt = getEffectiveCreationDate(issue.adjustCreationDate, issue.createdAt?.toString() || '');

  const [currentSeverity, setSeverity] = useState(issue.severity);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const { who } = getIssueAuditData(issue, ActivityOrderBy.MostRecentReported);

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={styles.dropdownContainer}>
          <TypeDropdown
            value={severityToVariant[currentSeverity]}
            onChange={handleSeverityChange}
            disabled={isSeverityDisabled()}
            showLabel={false}
            testID="issue-detail-severity-dropdown"
            // Only show the three main severity types for issues (matching old implementation)
            availableTypes={['attention', 'blocker', 'done']}
          />
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.titleRow}>
            <Text style={[styles.titleStyle]} numberOfLines={1} ellipsizeMode="tail">{getProcessedTitle()}</Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoText}>
              {issue.asset?.name || issue.workspace.name}
            </Text>
            <HeaderDot />
            <Text style={styles.infoText}>{who}</Text>
            <HeaderDot />
            <Text style={styles.infoText}>{formatShortDate(createdAt)}</Text>
          </View>
        </View>
        <View>
          {!props.hideMenu && (
            <View style={styles.menuContainer}>
              {/* Show pinned icon if issue is pinned */}
              {issue.isPinned && (
                <MaterialCommunityIcons
                  name="pin"
                  color={Colors.black}
                  size={20}
                  style={styles.pinnedIcon}
                />
              )}

              <IssueMenu
                issue={issue}
                hasReminder={hasReminder}
                onIssueDelete={props.onIssueDelete}
                onPrint={props.onPrint}
              />
            </View>
          )}
        </View>
      </View>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        workflow && <ReminderSummary workflow={workflow} issue={props.issue} />
      )}
    </View>
  );
};

export const IssueHeader = (props: IssueHeaderProps) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueHeaderOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  return <IssueHeaderBase {...props} />;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderBottomColor: Colors.grayDisabled,
    borderBottomWidth: 0.5,
    minHeight: 80,
    ...Platform.select({
      web: {
        zIndex: 1,
      },
    }),
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 20,
    overflow: 'visible' as const,
    minHeight: 80,
    zIndex: 1,
  },
  contentContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    ...Layout.container,
    width: '100%',
    marginLeft: 4,
    height: 50,
    overflow: 'hidden',
  },
  dropdownContainer: {
    height: 42,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  titleStyle: {
    flex: 1,
    color: Colors.black,
    padding: 0,
    margin: 0,
    fontSize: 18,
    fontWeight: '600',
    textAlignVertical: 'bottom',
  },
  infoRow: {
    flexDirection: 'row',
    width: '100%',
    height: 14,
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    height: 25,
    marginBottom: 2,
  },
  infoText: {
    color: Colors.grayFill,
    textAlignVertical: 'center',
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: -0.4,
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;